import {
  createPathologyOrder,
  getPathologyPanelById,
  getPathologyPanelsList,
  PathologyOrder,
  PathologyPanel,
  getPathologyOrderDetails,
  initiatePathologyPayment,
  PathologyPaymentReceipt,
  getPathologyPaymentReceipt,
  PathologyPaymentStatus,
  getMemberProfile,
  MemberProfile,
  MembershipDependent,
  getMembershipDependents,
  updateMembershipDependents,
  MemberServiceOfferingUsage,
  getMemberServiceOfferingUsage,
  MemberServiceOfferingType,
  MemberServiceOfferingStatus,
  oneVoucherPayment,
  ApplyVoucherResponse,
  applyDiscountVoucher,
} from "@/services/core-api-adapter";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import { assign, fromPromise, setup } from "xstate";

const POLLING_INTERVAL_IN_MILLISECONDS =
  Number(import.meta.env.VITE_APP_PATHOLOGY_POLLING_INTERVAL) || 5000;
const ORDER_DETAILS_MAX_POLLING_TIME_IN_SECONDS = 60;

interface Context {
  fetchOrderDetailsResponse: PathologyOrder | null;
  createOrderResponse: PathologyOrder | null;
  memberProfileResponse: MemberProfile | null;
  membersList: Array<MembershipDependent>;
  currentUserMemberId: string | null;
  selectedDependent: MembershipDependent | null;
  initiatePaymentResponse: any;
  bloodPanelListResponse: PathologyPanel[];
  paymentReceiptResponse: PathologyPaymentReceipt | null;
  bloodPanelDetailsResponse: any;
  selectedBloodPanel: PathologyPanel | null;
  panelIdQueryParam: string | null;
  orderIdQueryParam: string | null;
  orderDetailsPollStartTime: Date | null;
  memberServiceOfferingUsages: MemberServiceOfferingUsage[] | null;
  selectedVoucher: MemberServiceOfferingUsage | null;
  isVoucherApplied: boolean;
  oneVoucherPin?: string;
  oneVoucherRedemptionError: any;
  oneVoucherRedemptionSuccess: boolean;
  selectedDiscountVoucher: MemberServiceOfferingUsage | null;
  capturedDiscountVoucherCode?: string | null;
  applyDiscountVoucherResponse?: ApplyVoucherResponse | null;
  applyDiscountVoucherResponseError: any;
  isUseVoucherLater: boolean;
}

const initialContextValues: Context = {
  fetchOrderDetailsResponse: null,
  createOrderResponse: null,
  memberProfileResponse: null,
  membersList: [],
  currentUserMemberId: null,
  selectedDependent: null,
  bloodPanelListResponse: [],
  initiatePaymentResponse: null,
  paymentReceiptResponse: null,
  bloodPanelDetailsResponse: {},
  selectedBloodPanel: null,
  panelIdQueryParam: null,
  orderIdQueryParam: null,
  orderDetailsPollStartTime: null,
  memberServiceOfferingUsages: null,
  selectedVoucher: null,
  isVoucherApplied: false,
  oneVoucherPin: undefined,
  oneVoucherRedemptionError: null,
  oneVoucherRedemptionSuccess: false,
  selectedDiscountVoucher: null,
  capturedDiscountVoucherCode: null,
  applyDiscountVoucherResponse: null,
  applyDiscountVoucherResponseError: null,
  isUseVoucherLater: false,
};

export const eventNames = {
  BACK_BUTTON_CLICKED: "BACK_BUTTON_CLICKED",
  BUY_NOW_BUTTON_CLICKED: "BUY_NOW_BUTTON_CLICKED",
  CONTINUE_BUTTON_CLICKED: "CONTINUE_BUTTON_CLICKED",
  USER_REDIRECTED: "USER_REDIRECTED",
  BLOOD_PANEL_SELECTED: "BLOOD_PANEL_SELECTED",
  GO_HOME_BUTTON_CLICKED: "GO_HOME_BUTTON_CLICKED",
  VIEW_RECEIPT_BUTTON_CLICKED: "VIEW_RECEIPT_BUTTON_CLICKED",
  VIEW_RESULTS_BUTTON_CLICKED: "VIEW_RESULTS_BUTTON_CLICKED",
  HOW_TO_PREPARE_BUTTON_CLICKED: "HOW_TO_PREPARE_BUTTON_CLICKED",
  PAYMENT_SUCCESS_RECEIVED: "PAYMENT_SUCCESS_RECEIVED",
  DEPENDANT_SELECTED: "DEPENDANT_SELECTED",
  SKIP_BUTTON_CLICKED: "SKIP_BUTTON_CLICKED",
  CARD_PAYMENT_BUTTON_CLICKED: "CARD_PAYMENT_BUTTON_CLICKED",
  ONE_VOUCHER_BUTTON_CLICKED: "ONE_VOUCHER_BUTTON_CLICKED",
  VOUCHER_BUTTON_CLICKED: "VOUCHER_BUTTON_CLICKED",
  ONE_VOUCHER_CONTINUE_BUTTON_CLICKED: "ONE_VOUCHER_CONTINUE_BUTTON_CLICKED",
  DISCOUNT_VOUCHER_SELECTED: "DISCOUNT_VOUCHER_SELECTED",
  APPLY_DISCOUNT_VOUCHER: "APPLY_DISCOUNT_VOUCHER",
  ADD_A_DISCOUNT_VOUCHER_CLICKED: "ADD_A_DISCOUNT_VOUCHER_CLICKED",
};

export const pathologyFlowStateMachine = setup({
  types: {
    context: {} as Context,
  },
  actions: {
    addOrderDetailsToContext: function () {
      return {};
    },

    addMembersProfileResponseToContext: assign(({ event }) => {
      return { memberProfileResponse: event.output };
    }),

    addBloodPanelListResponseToContext: assign(({ event }) => {
      return {
        bloodPanelListResponse: event.output,
      };
    }),

    addSelectedBloodPanelToContext: assign(({ event }) => {
      return {
        selectedBloodPanel: event.output,
      };
    }),

    addCreateOrderResponseToContext: assign(({ event }) => {
      return {
        createOrderResponse: event.output,
      };
    }),

    addInitiatePaymentResponseToContext: assign(({ event, context }) => {
      return {
        initiatePaymentResponse: event.output,
        oneVoucherRedemptionSuccess: !!context.oneVoucherPin,
      };
    }),

    addOrderDetailsPollStartTime: assign(() => {
      return {
        orderDetailsPollStartTime: new Date(),
      };
    }),

    addOrderDetailsResponseToContext: assign(({ event, context }) => {
      const fetchOrderDetailsResponse: PathologyOrder = event.output;
      let selectedDependant: MembershipDependent | null = null;
      if (
        context.orderIdQueryParam &&
        isFeatureEnabled(
          FeatureToggleIdentifier.ENABLE_DEPENDANT_SERVICE_OFFERING
        )
      ) {
        selectedDependant =
          context.membersList.find((member: MembershipDependent) => {
            return (
              member.memberId === fetchOrderDetailsResponse?.testSubjectMemberId
            );
          }) || null;
      }
      return {
        fetchOrderDetailsResponse: fetchOrderDetailsResponse,
        ...(selectedDependant && { selectedDependent: selectedDependant }),
      };
    }),

    addPaymentReceiptResponseToContext: assign(({ event }) => {
      return {
        paymentReceiptResponse: event.output,
      };
    }),

    addSelectedDependentToContext: assign(({ event }) => {
      return { selectedDependent: event.input };
    }),

    addMembersListToContext: assign(({ event }) => {
      return { membersList: event.output };
    }),
    addMemberServiceOfferingUsageResponseToContext: assign(({ event }) => {
      return { memberServiceOfferingUsages: event.output.body };
    }),
    addVoucherToContext: assign(({ event }) => {
      return { selectedVoucher: event.input };
    }),
    addIsVoucherAppliedToContext: assign(() => {
      return { isVoucherApplied: true };
    }),
    getBloodPanelFromSelectedVoucher: assign(({ context }) => {
      const { bloodPanelListResponse, selectedVoucher } = context;

      if (!bloodPanelListResponse || !selectedVoucher) {
        console.error(
          "Either bloodPanelListResponse or selectedVoucher is missing"
        );
        return context;
      }

      const matchingBloodPanel = bloodPanelListResponse.find(
        (panel) => panel.panelChargeCode === selectedVoucher.chargeCode
      );

      if (!matchingBloodPanel) {
        console.error(
          `No matching blood panel found for chargeCode: ${selectedVoucher.chargeCode}`
        );
        return context;
      }

      return {
        ...context,
        selectedBloodPanel: matchingBloodPanel,
      };
    }),
    addOneVoucherPinToContext: assign(({ event }) => {
      return {
        oneVoucherPin: event.input,
      };
    }),
    addInitiatePaymentResponseErrorToContext: assign(({ event }) => {
      return {
        oneVoucherRedemptionError: event.error,
      };
    }),
    addSelectedDiscountVoucherToContext: assign(({ event }) => {
      return { selectedDiscountVoucher: event.input || null };
    }),
    clearSelectedDiscountVoucher: assign(() => {
      return {
        selectedDiscountVoucher: null,
      };
    }),
    addCapturedDiscountVoucherCodeToContext: assign(({ event }) => {
      return {
        capturedDiscountVoucherCode: event.input.voucherCode || null,
        isUseVoucherLater: event.input.isUseVoucherLater || false,
      };
    }),
    addApplyDiscountVoucherResponseToContext: assign(({ event }) => {
      return {
        applyDiscountVoucherResponse: event.output || null,
        selectedDiscountVoucher: event.output?.memberOfferingUsage || null,
      };
    }),
    addApplyDiscountVoucherResponseErrorToContext: assign(({ event }) => {
      return { applyDiscountVoucherResponseError: event.error || null };
    }),
    removeApplyDiscountVoucherResponseErrorFromContext: assign(() => {
      return {
        applyDiscountVoucherResponseError: null,
        capturedDiscountVoucherCode: null,
        isUseVoucherLater: false,
      };
    }),
    clearCapturedDiscountVoucher: assign(() => {
      return {
        applyDiscountVoucherResponseError: null,
        applyDiscountVoucherResponse: null,
        capturedDiscountVoucherCode: null,
      };
    }),
    clearOnApplyDiscountVoucher: assign(() => {
      return {
        applyDiscountVoucherResponseError: null,
        applyDiscountVoucherResponse: null,
        selectedDiscountVoucher: null,
      };
    }),
  },
  actors: {
    getBloodPanelById: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        const panelId =
          input?.context?.panelIdQueryParam ||
          input?.context.selectedBloodPanel?.panelId;
        return getPathologyPanelById(panelId || "");
      }
    ),
    getMemberProfile: fromPromise(async () => {
      return await getMemberProfile();
    }),
    getBloodPanelList: fromPromise(async () => {
      return getPathologyPanelsList();
    }),
    createOrder: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        const memberId =
          input?.context?.selectedDependent?.memberId || undefined;
        return createPathologyOrder(
          input?.context?.selectedBloodPanel?.panelId || "",
          memberId
        );
      }
    ),
    initiatePayment: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        // memberId is always the main member (payer), regardless of who the patient is
        const memberId = input?.context?.currentUserMemberId || "";
        const orderId = input?.context?.createOrderResponse?.orderId || "";
        const amount = input?.context?.selectedBloodPanel?.amountTotal || 0;
        const memberServiceOfferingUsageId =
          input?.context?.selectedVoucher?.id ||
          input?.context?.selectedDiscountVoucher?.id;
        const oneVoucherPin = input?.context?.oneVoucherPin || "";

        const isDiscountVoucherFeatureEnabled = isFeatureEnabled(
          FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS
        );

        if (
          (input?.context?.isVoucherApplied &&
            input?.context?.selectedVoucher) ||
          (isDiscountVoucherFeatureEnabled &&
            input?.context?.selectedDiscountVoucher) ||
          input?.context?.oneVoucherPin
        ) {
          return initiatePathologyPayment(
            orderId,
            amount,
            memberId,
            memberServiceOfferingUsageId,
            oneVoucherPin
          );
        }

        return initiatePathologyPayment(orderId, amount, memberId);
      }
    ),
    fetchOrderDetails: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return getPathologyOrderDetails(
          input?.context?.orderIdQueryParam ||
            input?.context?.createOrderResponse?.orderId ||
            ""
        );
      }
    ),
    fetchPaymentReceipt: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return getPathologyPaymentReceipt(
          input?.context?.orderIdQueryParam ||
            input?.context?.createOrderResponse?.orderId ||
            ""
        );
      }
    ),
    updateMemberDependents: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return updateMembershipDependents({
          dependents: input.context.selectedDependent
            ? [input.context.selectedDependent]
            : [],
        });
      }
    ),
    getMemberships: fromPromise(async () => {
      return await getMembershipDependents();
    }),
    getMemberServiceOfferingUsage: fromPromise(async () => {
      return await getMemberServiceOfferingUsage(
        MemberServiceOfferingType.PATHOLOGY
      );
    }),
    oneVoucherRedemption: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return oneVoucherPayment(
          input?.context?.createOrderResponse?.orderId || "",
          input?.context?.oneVoucherPin || ""
        );
      }
    ),
    applyDiscountVoucher: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return await applyDiscountVoucher(
          input.context.capturedDiscountVoucherCode || "",
          input.context.currentUserMemberId || ""
        );
      }
    ),
  },
  guards: {
    hasBloodPanelIdInQueryParams: function ({ context }) {
      return context.panelIdQueryParam !== null;
    },
    hasOrderIdInQueryParams: function ({ context }) {
      return context.orderIdQueryParam !== null;
    },
    paymentHasFailed: function () {
      return true;
    },
    hasOrderNumber: function ({ context }) {
      return Boolean(context.fetchOrderDetailsResponse?.orderNumber);
    },
    hasNoOrderNumber: function () {
      return true;
    },
    orderDetailsPollTimeoutExceeded: function ({ context }) {
      const currentDate = new Date();
      const orderDetailsPollStartTime = context.orderDetailsPollStartTime;

      if (orderDetailsPollStartTime) {
        return (
          Number(currentDate.getTime() - orderDetailsPollStartTime.getTime()) /
            1000 >
          ORDER_DETAILS_MAX_POLLING_TIME_IN_SECONDS
        );
      }

      return false;
    },
    userHasSuccessfulPayment: function ({ context }) {
      const isPaymentSuccessful = Boolean(
        context.paymentReceiptResponse?.paymentSuccess
      );

      return isPaymentSuccessful;
    },
    userHasUnsuccessfulPayment: function ({ context }) {
      return (
        context.paymentReceiptResponse?.status ===
        PathologyPaymentStatus.PAYMENT_FAILED
      );
    },
    hasDependants: function ({ context }) {
      return context.membersList.length > 0;
    },
    isSelectedDependentNotMainMember: function ({ context }) {
      return (
        context.selectedDependent?.memberId !== context.currentUserMemberId
      );
    },
    isDependantServiceOfferingEnabled: function () {
      return isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_DEPENDANT_SERVICE_OFFERING
      );
    },
    isEnhancedPaymentOptionsEnabled: function () {
      return isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
      );
    },
    isVoucherAppliedInContext: function ({ context }) {
      return context.isVoucherApplied;
    },
    isVoucheringFeatureEnabledAndHasValidPromoCodeAndEnhancedPaymentOptionsDisabled:
      function ({ context }) {
        return (
          isFeatureEnabled(FeatureToggleIdentifier.ENABLE_VOUCHER_REDEMPTION) &&
          !isFeatureEnabled(
            FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
          ) &&
          (context.memberServiceOfferingUsages ?? []).some((voucher) => {
            const currentDate = new Date();
            const availableFrom = new Date(voucher.availableFrom);
            const availableTo = new Date(voucher.availableTo);
            return (
              voucher.usageStatus === MemberServiceOfferingStatus.ACTIVE &&
              currentDate >= availableFrom &&
              currentDate <= availableTo
            );
          })
        );
      },
    isSelectedVoucherMissing: ({ context }) => {
      return !context.selectedVoucher;
    },
    isOneVoucherPinInContext: ({ context }) => {
      return !!context.oneVoucherPin;
    },
    isOneVoucherPaymentSuccessful: ({ context }) => {
      return context.oneVoucherRedemptionSuccess;
    },
    isOneVoucherRedemptionErrorMessage: ({ context }) => {
      try {
        return !!JSON.parse(context.oneVoucherRedemptionError);
      } catch {
        return false;
      }
    },
    isDiscountVouchersFeatureEnabled: function () {
      return isFeatureEnabled(FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS);
    },
  },
  schemas: {},
  delays: {
    POLLING_INTERVAL: POLLING_INTERVAL_IN_MILLISECONDS,
  },
}).createMachine({
  context: initialContextValues,
  id: "pathologyFlow",
  initial: "showingBloodPanelListOrShowingBloodPanelDetails",
  states: {
    showingBloodPanelListOrShowingBloodPanelDetails: {
      entry: assign(({ event }) => {
        const entryContext = { ...initialContextValues };
        const {
          panelId = null,
          orderId = null,
          currentUserMemberId = null,
        } = event.input?.context ? event.input.context : {};

        return {
          ...entryContext,
          panelIdQueryParam: panelId,
          orderIdQueryParam: orderId,
          currentUserMemberId: currentUserMemberId,
        };
      }),
      always: [
        {
          target: "fetchingBloodPanelDetails",
          guard: {
            type: "hasBloodPanelIdInQueryParams",
          },
        },
        {
          target: "fetchingMemberDetailsOrFetchingPaymentReceipt",
          guard: {
            type: "hasOrderIdInQueryParams",
          },
        },
        {
          target: "fetchingMemberProfile",
        },
      ],
    },
    fetchingMemberDetailsOrFetchingPaymentReceipt: {
      always: [
        {
          target: "fetchingMemberDetails",
          guard: {
            type: "isDependantServiceOfferingEnabled",
          },
        },
        {
          target: "fetchingPaymentReceipt",
        },
      ],
    },
    fetchingBloodPanelDetails: {
      invoke: {
        input: ({ context }) => ({ context }),
        onDone: {
          target: "showingBloodPanelDetails",
          actions: {
            type: "addSelectedBloodPanelToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "getBloodPanelById",
      },
    },
    fetchingMemberProfile: {
      invoke: {
        id: "fetchingMemberProfile",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "fetchingMemberServiceOfferingUsage",
          actions: {
            type: "addMembersProfileResponseToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "getMemberProfile",
      },
    },
    fetchingMemberServiceOfferingUsage: {
      invoke: {
        id: "fetchingMemberServiceOfferingUsage",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "fetchingMemberDetailsOrFetchingBloodPanelList",
          actions: {
            type: "addMemberServiceOfferingUsageResponseToContext",
          },
        },
        onError: {
          target: "fetchingMemberDetailsOrFetchingBloodPanelList",
        },
        src: "getMemberServiceOfferingUsage",
      },
    },
    fetchingMemberDetailsOrFetchingBloodPanelList: {
      always: [
        {
          target: "fetchingMemberDetails",
          guard: {
            type: "isDependantServiceOfferingEnabled",
          },
        },
        {
          target: "fetchingBloodPanelList",
        },
      ],
    },
    fetchingMemberDetails: {
      invoke: {
        id: "fetchingMemberDetails",
        input: ({ context }) => ({ context }),
        onDone: {
          target:
            "selectingDependantsFetchingBloodPanelListOrFetchingPaymentReceipt",
          actions: {
            type: "addMembersListToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "getMemberships",
      },
    },
    selectingDependantsFetchingBloodPanelListOrFetchingPaymentReceipt: {
      always: [
        {
          target: "fetchingPaymentReceipt",
          guard: {
            type: "hasOrderIdInQueryParams",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "hasDependants",
          },
        },
        {
          target: "fetchingBloodPanelList",
        },
      ],
    },
    selectingDependents: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
        [eventNames.DEPENDANT_SELECTED]: {
          target: "updatingMemberDependentsOrFetchingBloodPanelList",
          actions: "addSelectedDependentToContext",
        },
      },
    },
    updatingMemberDependentsOrFetchingBloodPanelList: {
      always: [
        {
          target: "updatingMemberDependents",
          guard: {
            type: "isSelectedDependentNotMainMember",
          },
        },
        {
          target: "fetchingBloodPanelList",
        },
      ],
    },
    updatingMemberDependents: {
      invoke: {
        id: "updatingMemberDependents",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "fetchingBloodPanelList",
        },
        onError: {
          target: "error",
        },
        src: "updateMemberDependents",
      },
    },
    fetchingBloodPanelList: {
      invoke: {
        input: {},
        onDone: {
          target: "showingBloodPanelListOrPromptingVoucherSelection",
          actions: {
            type: "addBloodPanelListResponseToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "getBloodPanelList",
      },
    },
    showingBloodPanelDetails: {
      on: {
        BACK_BUTTON_CLICKED: [
          {
            target: "exit",
            guard: {
              type: "hasBloodPanelIdInQueryParams",
            },
          },
          {
            target: "showingBloodPanelList",
          },
        ],
        BUY_NOW_BUTTON_CLICKED: {
          target: "showingPrompt",
        },
      },
    },
    error: {
      on: {
        GO_HOME_BUTTON_CLICKED: {
          target: "exit",
        },
      },
    },
    showingBloodPanelListOrPromptingVoucherSelection: {
      always: [
        {
          target: "promptingVoucherSelection",
          guard: {
            type: "isVoucheringFeatureEnabledAndHasValidPromoCodeAndEnhancedPaymentOptionsDisabled",
          },
        },
        {
          target: "showingBloodPanelList",
        },
      ],
    },
    promptingVoucherSelection: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "showingBloodPanelList",
        },
        CONTINUE_BUTTON_CLICKED: [
          {
            target: "showingPromptOrBloodPanelList",
            actions: { type: "addVoucherToContext" },
          },
        ],
        SKIP_BUTTON_CLICKED: {
          target: "exit",
        },
      },
    },
    showingPromptOrBloodPanelList: {
      always: [
        {
          target: "showingBloodPanelList",
          guard: {
            type: "isSelectedVoucherMissing",
          },
        },
        {
          target: "showingPrompt",
          actions: [
            { type: "addIsVoucherAppliedToContext" },
            { type: "getBloodPanelFromSelectedVoucher" },
          ],
        },
      ],
    },
    showingBloodPanelList: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "exit",
        },
        USER_REDIRECTED: {
          target: "exit",
        },
        BLOOD_PANEL_SELECTED: {
          target: "fetchingBloodPanelDetails",
          actions: { type: "addSelectedBloodPanelToContext" },
        },
      },
    },
    exit: {
      type: "final",
    },
    showingPrompt: {
      entry: "clearCapturedDiscountVoucher",
      on: {
        BACK_BUTTON_CLICKED: {
          target: "showingBloodPanelDetails",
        },
        CONTINUE_BUTTON_CLICKED: [
          {
            target: "refreshingPaymentServiceOfferings",
            guard: "isEnhancedPaymentOptionsEnabled",
          },
          {
            target: "creatingOrder",
          },
        ],
      },
    },
    refreshingPaymentServiceOfferings: {
      invoke: {
        id: "refreshingPaymentServiceOfferings",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "showingPaymentOptions",
          actions: {
            type: "addMemberServiceOfferingUsageResponseToContext",
          },
        },
        onError: {
          target: "showingPaymentOptions",
        },
        src: "getMemberServiceOfferingUsage",
      },
    },
    showingPaymentOptions: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "showingPrompt",
          actions: [{ type: "clearCapturedDiscountVoucher" }],
        },
        CARD_PAYMENT_BUTTON_CLICKED: {
          target: "creatingOrder",
        },
        VOUCHER_BUTTON_CLICKED: {
          target: "selectingVoucher",
        },
        ONE_VOUCHER_BUTTON_CLICKED: {
          target: "showingOneVoucherRedemption",
        },
        DISCOUNT_VOUCHER_SELECTED: {
          actions: { type: "addSelectedDiscountVoucherToContext" },
        },
        [eventNames.APPLY_DISCOUNT_VOUCHER]: {
          target: "applyingDiscountVoucher",
          actions: { type: "addCapturedDiscountVoucherCodeToContext" },
          guard: "isDiscountVouchersFeatureEnabled",
        },
        [eventNames.ADD_A_DISCOUNT_VOUCHER_CLICKED]: {
          actions: {
            type: "removeApplyDiscountVoucherResponseErrorFromContext",
          },
        },
      },
    },
    applyingDiscountVoucher: {
      entry: "clearOnApplyDiscountVoucher",
      invoke: {
        id: "applyDiscountVoucher",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "showingPaymentOptions",
          actions: { type: "addApplyDiscountVoucherResponseToContext" },
        },
        onError: {
          target: "showingPaymentOptions",
          actions: { type: "addApplyDiscountVoucherResponseErrorToContext" },
        },
        src: "applyDiscountVoucher",
      },
    },
    selectingVoucher: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "showingPaymentOptions",
        },
        CONTINUE_BUTTON_CLICKED: {
          target: "creatingOrder",
          actions: [
            { type: "addVoucherToContext" },
            { type: "addIsVoucherAppliedToContext" },
            { type: "getBloodPanelFromSelectedVoucher" },
          ],
        },
      },
    },
    showingOneVoucherRedemption: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "showingPaymentOptions",
        },
        [eventNames.ONE_VOUCHER_CONTINUE_BUTTON_CLICKED]: {
          target: "creatingOrder",
          actions: "addOneVoucherPinToContext",
        },
      },
    },
    creatingOrder: {
      invoke: {
        src: "createOrder",
        input: ({ context }) => ({ context }),
        onDone: [
          {
            target: "initiatingPaymentWithVoucher",
            guard: {
              type: "isVoucherAppliedInContext",
            },
            actions: {
              type: "addCreateOrderResponseToContext",
            },
          },
          {
            target: "initiatingPaymentWithVoucher",
            guard: {
              type: "isOneVoucherPinInContext",
            },
            actions: {
              type: "addCreateOrderResponseToContext",
            },
          },
          {
            target: "initiatingPayment",
            actions: {
              type: "addCreateOrderResponseToContext",
            },
          },
        ],
        onError: {
          target: "error",
        },
      },
    },
    initiatingPaymentWithVoucher: {
      invoke: {
        input: ({ context }) => ({ context }),
        onDone: {
          target: "fetchingPaymentReceipt",
          actions: {
            type: "addInitiatePaymentResponseToContext",
          },
        },
        onError: {
          target: "handleOneVoucherRedemptionError",
          actions: {
            type: "addInitiatePaymentResponseErrorToContext",
          },
        },
        src: "initiatePayment",
      },
    },
    handleOneVoucherRedemptionError: {
      always: [
        {
          target: "showingOneVoucherRedemption",
          guard: {
            type: "isOneVoucherRedemptionErrorMessage",
          },
        },
        {
          target: "error",
        },
      ],
    },
    initiatingPayment: {
      invoke: {
        input: ({ context }) => ({ context }),
        onDone: {
          target: "breakoutScreen",
          actions: {
            type: "addInitiatePaymentResponseToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "initiatePayment",
      },
    },
    breakoutScreen: {
      on: {
        USER_REDIRECTED: {
          target: "waitingForOrderOutcome",
        },
        BACK_BUTTON_CLICKED: [
          {
            target: "refreshingPaymentServiceOfferings",
            guard: {
              type: "isEnhancedPaymentOptionsEnabled",
            },
          },
          {
            target: "showingPrompt",
          },
        ],
      },
    },
    waitingForOrderOutcome: {
      after: {
        POLLING_INTERVAL: {
          target: "fetchingPaymentReceipt",
        },
      },
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    fetchingPaymentReceipt: {
      invoke: {
        id: "fetchPaymentReceipt",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "processingPaymentOutcome",
          actions: {
            type: "addPaymentReceiptResponseToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "fetchPaymentReceipt",
      },
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    processingPaymentOutcome: {
      always: [
        {
          target: "fetchingOrderDetails",
          guard: {
            type: "userHasSuccessfulPayment",
          },
          actions: {
            type: "addOrderDetailsPollStartTime",
          },
        },
        {
          target: "fetchingOrderDetails",
          guard: {
            type: "isOneVoucherPaymentSuccessful",
          },
          actions: {
            type: "addOrderDetailsPollStartTime",
          },
        },
        {
          target: "paymentFailed",
          guard: {
            type: "userHasUnsuccessfulPayment",
          },
        },
        {
          target: "waitingForOrderOutcome",
        },
      ],
    },
    fetchingOrderDetails: {
      invoke: {
        id: "fetchOrderDetails",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "processingOrderDetailsOutcome",
          actions: {
            type: "addOrderDetailsResponseToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "fetchOrderDetails",
      },
    },
    processingOrderDetailsOutcome: {
      always: [
        {
          target: "showingOrderSummary",
          guard: {
            type: "hasOrderNumber",
          },
        },
        {
          target: "showingOrderSummary",
          guard: {
            type: "orderDetailsPollTimeoutExceeded",
          },
        },
        {
          target: "waitingForOrderDetails",
        },
      ],
    },
    waitingForOrderDetails: {
      after: {
        POLLING_INTERVAL: {
          target: "fetchingOrderDetails",
        },
      },
    },
    paymentFailed: {
      on: {
        GO_HOME_BUTTON_CLICKED: {
          target: "exit",
        },
        PAYMENT_SUCCESS_RECEIVED: {
          target: "fetchingPaymentReceipt",
        },
      },
    },
    showingOrderSummary: {
      on: {
        VIEW_RECEIPT_BUTTON_CLICKED: {
          target: "showingPaymentReceipt",
        },
        VIEW_RESULTS_BUTTON_CLICKED: {
          target: "showingWhatsappBreakout",
        },
        HOW_TO_PREPARE_BUTTON_CLICKED: {
          target: "showingHowToPreparePrompt",
        },
        BACK_BUTTON_CLICKED: [
          {
            target: "exitToMyPurchases",
            guard: {
              type: "hasOrderIdInQueryParams",
            },
          },
          {
            target: "exit",
          },
        ],
      },
    },
    exitToMyPurchases: {
      type: "final",
    },
    showingWhatsappBreakout: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "showingOrderSummary",
        },
      },
    },
    showingPaymentReceipt: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "showingOrderSummary",
        },
      },
    },
    showingHowToPreparePrompt: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "showingOrderSummary",
        },
      },
    },
  },
});
